// src/contexts/UserContext.js
import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useState } from 'react'
import { isAdminRole, isSuperadminRole } from 'utils/roles'

// Crea un contexto para gestionar el estado del usuario actual
export const UserContext = createContext(null)

// true si el JWT ya ha caducado (o no se puede leer). El backend lo emite con
// una validez de 8 h; sin esta comprobación la app seguía "logada" con un token
// muerto y todas las peticiones protegidas fallaban en silencio.
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return !payload.exp || payload.exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

const readStoredUser = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('user'))
    if (stored?.token && isTokenExpired(stored.token)) return {}
    return stored
  } catch {
    return null
  }
}

// Proveedor del contexto del usuario actual. Envuelve los componentes hijos y proporciona las funciones para manejar el usuario actual
export const UserProvider = ({ children }) => {
  // Estado para el usuario actual, inicializado desde localStorage si existe (y su sesión no ha caducado)
  const [user, setUserState] = useState(readStoredUser)

  // Guarda el usuario en localStorage ANTES de actualizar el estado: useFetch lee
  // el token de ahí, y los efectos de los hijos (p. ej. CompanyProvider) se
  // ejecutan antes que los de este proveedor, así que irían con el token viejo.
  const setUser = (next) => {
    setUserState(prev => {
      const value = typeof next === 'function' ? next(prev) : next
      localStorage.setItem('user', JSON.stringify(value))
      return value
    })
  }

  useEffect(() => {

    // Si el usuario tiene rol 2 o 3, aseguramos que su project_id
    // está guardado en localStorage para que ProjectsContext lo use
    if (user && (user.role === 2 || user.role === 3) && user.project_id) {
      localStorage.setItem('selectedProjectId', user.project_id)
    }
  }, [user])

  // Función para verificar si el usuario es administrador (rol 1)
  const isAdmin = () => user && isAdminRole(user.role)

  // Función para verificar si el usuario tiene un rol específico
  const hasRole = (role) => user && user.role === role

  // Función para comprobar permisos según el rol
  const hasPermission = (requiredRole) => {
    if (!user) return false
    
    // Si requiredRole es un array, comprobamos si el rol del usuario está incluido
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(user.role)
    }
    // Si requiredRole es un número, comprobamos si el rol del usuario coincide
    return user.role === requiredRole
  }

  // Función para filtrar usuarios según el rol y proyecto del usuario actual
  const filterUsersByProject = (usersList) => {
    if (!user || !usersList || !Array.isArray(usersList)) return []
    
    console.log("Filtrando usuarios por proyecto:", usersList);
    
    // Si es admin, puede ver todos los usuarios
    if (isAdminRole(user.role)) {
      return usersList
    }
    
    // Si es editor (rol 2), solo puede ver usuarios de su proyecto
    if (user.role === 2 && user.project_name) {
      // Filtrar por nombre de proyecto
      const filtered = usersList.filter(u => u.project_name === user.project_name)
      console.log("Usuarios filtrados para editor por project_name:", filtered);
      return filtered
    }
    
    // Otros roles ven solo lo que su rol permite
    return usersList
  }

  // Función para determinar si un usuario puede ser editado por el usuario actual
  const canEditUser = (targetUser) => {
    if (!user || !targetUser) return false
    
    console.log("Verificando si puede editar:", targetUser);
    
    // Administradores pueden editar a cualquiera
    if (isAdminRole(user.role)) return true
    
    // Editores (rol 2) solo pueden editar usuarios Viewer (rol 3) de su mismo proyecto
    if (user.role === 2) {
      // Solo pueden editar usuarios con rol 3 (Viewer) que pertenezcan a su mismo proyecto
      return (
        targetUser.role === 3 && 
        targetUser.project_name === user.project_name
      )
    }
    
    // Otros roles no pueden editar usuarios
    return false
  }

  return (
    // Proporciona el estado y las funciones del usuario actual a los componentes hijos
    <UserContext.Provider 
      value={{ 
        user, 
        setUser,
        isAdmin,
        // Superadmin: todo lo del admin + Servidores y registro de actividad
        isSuperadmin: () => !!user && isSuperadminRole(user.role),
        hasRole,
        hasPermission,
        // Función para obtener el rol del usuario (útil para componentes)
        userRole: user ? user.role : null,
        // Nuevas funciones
        filterUsersByProject,
        canEditUser
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

// Hook personalizado para usar el contexto del usuario actual de forma más sencilla
export const useUser = () => useContext(UserContext)

// Define los tipos de las props que recibe el componente actual
UserProvider.propTypes = {
  children: PropTypes.any
}